/**
 * CreateOntologyPanel — right-anchored fixed overlay listing ontology creation options.
 *
 * Follows the same pattern as AddDocumentsPanel in the Knowledge Base module.
 * The caller owns the hidden file input for JSON-LD import; this panel just
 * calls onImportJsonLd() which triggers importInputRef.current?.click() on the page.
 */
import { IconButton } from '@carbon/react';
import {
  Close,
  Edit,
  WatsonHealth3DCursor,
  Catalog,
  DocumentImport,
  Document,
  ChevronRight,
  type CarbonIconType,
} from '@carbon/icons-react';

export interface CreateOntologyPanelProps {
  open: boolean;
  onClose: () => void;
  onScratch: () => void;
  onPrompt: () => void;
  onImportJsonLd: () => void;
  onKnowledgeBase: () => void;
}

export function CreateOntologyPanel({
  open,
  onClose,
  onScratch,
  onPrompt,
  onImportJsonLd,
  onKnowledgeBase,
}: CreateOntologyPanelProps) {
  if (!open) return null;

  const rows: {
    icon: CarbonIconType;
    title: string;
    subtitle: string;
    onClick: () => void;
    disabled?: boolean;
  }[] = [
    {
      icon: Edit,
      title: 'Start from Scratch',
      subtitle: 'Create a new empty ontology and build it manually',
      onClick: () => { onClose(); onScratch(); },
    },
    {
      icon: WatsonHealth3DCursor,
      title: 'Generate with AI',
      subtitle: 'Describe your domain and let AI generate an initial structure',
      onClick: () => { onClose(); onPrompt(); },
    },
    {
      icon: Catalog,
      title: 'Start from Catalog',
      subtitle: 'Browse pre-built ontology templates — coming soon',
      onClick: () => {},
      disabled: true,
    },
    {
      icon: DocumentImport,
      title: 'Import JSON-LD',
      subtitle: 'Import an existing ontology from a JSON-LD export file',
      onClick: () => { onClose(); onImportJsonLd(); },
    },
    {
      icon: Document,
      title: 'Start from Knowledge Base',
      subtitle: 'Extract concepts and relationships from your documents',
      onClick: () => { onClose(); onKnowledgeBase(); },
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.2)',
          zIndex: 8999,
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: '320px',
          background: 'var(--cds-layer-01)',
          borderLeft: '1px solid var(--cds-border-subtle)',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.08)',
          zIndex: 9000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem',
            borderBottom: '1px solid var(--cds-border-subtle)',
          }}
        >
          <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--cds-text-primary)' }}>
            New Ontology
          </span>
          <IconButton kind="ghost" size="sm" label="Close panel" onClick={onClose}>
            <Close />
          </IconButton>
        </div>

        {/* Rows */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {rows.map(({ icon: Icon, title, subtitle, onClick, disabled }) => (
            <SourceRow
              key={title}
              icon={<Icon size={20} />}
              title={title}
              subtitle={subtitle}
              onClick={onClick}
              disabled={disabled}
            />
          ))}
        </div>
      </div>
    </>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function SourceRow({
  icon,
  title,
  subtitle,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) onClick();
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
      onMouseEnter={(e) => {
        if (!disabled) (e.currentTarget as HTMLDivElement).style.background = 'var(--cds-layer-hover-01)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = '';
      }}
    >
      {/* Icon badge */}
      <div
        style={{
          width: '40px',
          height: '40px',
          flexShrink: 0,
          background: 'var(--cds-layer-02)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--cds-text-primary)',
        }}
      >
        {icon}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: 'var(--cds-text-primary)', fontWeight: 500 }}>{title}</div>
        <div style={{ color: 'var(--cds-text-secondary)', fontSize: '0.75rem' }}>{subtitle}</div>
      </div>

      {/* Chevron */}
      {!disabled && (
        <ChevronRight size={16} style={{ color: 'var(--cds-text-secondary)', flexShrink: 0 }} />
      )}
    </div>
  );
}

// Made with Bob
